import React, {useEffect, useState} from 'react';
import {useLocation, Navigate, useNavigate} from 'react-router-dom';
import axiosInstance, {endpoints} from 'packages/common/utils/axios';
import 'packages/common/locales/i18n';
import 'simplebar-react/dist/simplebar.min.css';
import 'react-quill/dist/quill.snow.css';
import 'react-lazy-load-image-component/src/effects/blur.css';
/* import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/captions.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css'; */
/* import 'mapbox-gl/dist/mapbox-gl.css'; */
/* import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css'; */
// routes
import Router from './routes';
// theme
import ThemeProvider from 'packages/common/theme';
// locales
import {LocalizationProvider} from 'packages/common/locales';
// hooks
import {useScrollToTop} from 'packages/common/hooks/useScrollToTop';
// components
import ProgressBar from 'packages/common/components/progress-bar';
import {MotionLazy} from 'packages/common/components/animate/motion-lazy';
import {SnackbarProvider} from 'packages/common/components/snackbar';
import {SettingsProvider, SettingsDrawer} from 'packages/common/components/settings';
import {NetworkStatusBanner} from 'packages/common/components/network-status-banner';
import {BOTTOM_NAV_HEIGHT} from 'packages/common/layouts/app/bottom-nav';
// pwa components
import PullToRefresh from './src/pwa/components/PullToRefresh';
import AutoUpdateWatcher from './src/pwa/components/AutoUpdateWatcher';
// providers
import {SWRProvider} from 'packages/common/providers/SWRProvider';
import {CurrencyProvider} from 'packages/common/contexts/currency-context/CurrencyContext';

/**
 * Composant principal de l'application. C'est le point d'entrée de l'application React.
 * Ce composant initialise les providers pour les thèmes, les settings, et l'authentification.
 * Il englobe également les composants principaux tels que le router, la barre de progression, le tiroir de settings, etc.
 * Le hook `useScrollToTop` est appelé pour gérer le défilement automatique vers le haut lors de la navigation entre les pages.
 *
 * @return {JSX.Element}
 */
export default function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMaintenance, setIsMaintenance] = useState(false);

  useScrollToTop();

  // Vérification du mode maintenance
  useEffect(() => {
    const checkMaintenanceMode = async () => {
      try {
        const response = await axiosInstance.get(endpoints.public.maintenance.check, {
          timeout: 10000, // 10 secondes de délai d'attente
        });
        setIsMaintenance(response.data.isMaintenance);
      } catch (error) {
        if (error.response) {
          // Gestion des erreurs avec réponse du serveur
          if (error.response.status === 503) {
            setIsMaintenance(true);
          } else if (error.response.status === 504) {
            console.error('Le serveur a mis trop de temps à répondre (504).');
          } else {
            console.error('Erreur lors de la vérification du mode maintenance:', error);
          }
        } else {
          // Gestion des erreurs sans réponse (par exemple, si le serveur est inaccessible)
          console.error('Erreur de réseau ou de serveur inaccessible:', error);
        }
      }
    };

    // Appel initial
    checkMaintenanceMode();

    // Si sur la page de maintenance, vérifier périodiquement
    if (location.pathname === '/maintenance') {
      const intervalId = setInterval(() => {
        checkMaintenanceMode();
      }, 5000);

      return () => clearInterval(intervalId);
    }
  }, [location.pathname]);

  // Si la maintenance est terminée, redirige l'utilisateur vers la page d'origine
  useEffect(() => {
    if (!isMaintenance && location.pathname === '/maintenance') {
      navigate('/'); // Redirige vers la page d'accueil ou la page d'origine
    }
  }, [isMaintenance, location.pathname, navigate]);

  if (isMaintenance && location.pathname !== '/maintenance') {
    return <Navigate to="/maintenance" replace />;
  }

  return (
    <SWRProvider>
      <CurrencyProvider>
        <LocalizationProvider>
          <SettingsProvider
            defaultSettings={{
              themeMode: 'light', // 'light' | 'dark'
              themeDirection: 'ltr', //  'rtl' | 'ltr'
              themeContrast: 'default', // 'default' | 'bold'
              themeLayout: 'vertical', // 'vertical' | 'horizontal' | 'mini'
              themeColorPresets: 'default', // 'default' | 'cyan' | 'purple' | 'blue' | 'orange' | 'red'
              themeStretch: false,
            }}
          >
            <ThemeProvider>
              <MotionLazy>
                <SnackbarProvider>
                  <AutoUpdateWatcher />
                  <NetworkStatusBanner bottomOffset={BOTTOM_NAV_HEIGHT} />
                  <PullToRefresh />
                  <SettingsDrawer />
                  <ProgressBar />
                  <Router />
                </SnackbarProvider>
              </MotionLazy>
            </ThemeProvider>
          </SettingsProvider>
        </LocalizationProvider>
      </CurrencyProvider>
    </SWRProvider>
  );
}
